import React, { useEffect, useMemo, useState } from "react";
import {
    BookOpenText,
    ChevronDown,
    ChevronRight,
    Search,
    RefreshCcw,
    HelpCircle,
    Calendar,
    LayoutGrid,
} from "lucide-react";
import { getTopicsOverview } from "../../utils/admin";

const SectionHeader = ({ children, accent = "from-[#2148C0] to-blue-400" }) => (
    <h3 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center">
        <div className={`w-2 h-7 md:h-8 bg-gradient-to-b ${accent} rounded-full mr-3`} />
        {children}
    </h3>
);

const TopicsPage = () => {
    const [data, setData] = useState([]);
    const [totals, setTotals] = useState({ subjects: 0, topics: 0, questions: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [query, setQuery] = useState("");
    const [expandedSubjects, setExpandedSubjects] = useState({});
    const [expandedYears, setExpandedYears] = useState({});

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("token");
            const res = await getTopicsOverview(token);
            setData(res?.data || []);
            setTotals(res?.totals || { subjects: 0, topics: 0, questions: 0 });
        } catch (err) {
            console.error(err);
            setError("Failed to load topics overview.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return data;
        return data
            .map((s) => ({
                ...s,
                years: s.years
                    .map((y) => ({
                        ...y,
                        topics: y.topics.filter((t) => t.topic.toLowerCase().includes(q)),
                    }))
                    .filter(
                        (y) =>
                            y.topics.length ||
                            String(y.yearLabel).toLowerCase().includes(q) ||
                            s.subject.toLowerCase().includes(q)
                    ),
            }))
            .filter((s) => s.subject.toLowerCase().includes(q) || s.years.length > 0);
    }, [data, query]);

    const toggleSubject = (subject) =>
        setExpandedSubjects((prev) => ({ ...prev, [subject]: !prev[subject] }));
    const toggleYear = (key) =>
        setExpandedYears((prev) => ({ ...prev, [key]: !prev[key] }));

    return (
        <div>
            {/* Hero */}
            <div className="mb-8 bg-gradient-to-r from-[#2148C0] to-blue-600 rounded-2xl p-6 md:p-8 text-white relative overflow-hidden shadow-lg shadow-[#2148C0]/20">
                <div className="absolute -top-20 -right-20 w-48 h-48 bg-white/10 rounded-full" />
                <div className="absolute -bottom-16 -left-16 w-32 h-32 bg-white/5 rounded-full" />
                <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 border border-white/20 text-white text-xs font-medium mb-3">
                            <BookOpenText size={13} /> Question Bank
                        </div>
                        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
                            Topics by Subject &amp; Year
                        </h1>
                        <p className="text-blue-100 mt-1">
                            Every topic, grouped by subject and class/year, with question counts.
                        </p>
                    </div>
                    <button
                        onClick={load}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white text-[#2148C0] hover:bg-blue-50 text-sm font-semibold shadow"
                    >
                        <RefreshCcw size={15} /> Refresh
                    </button>
                </div>
            </div>

            {/* Summary tiles */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <SummaryTile
                    icon={LayoutGrid}
                    label="Subjects"
                    value={totals.subjects}
                    color="bg-blue-50 text-[#2148C0]"
                />
                <SummaryTile
                    icon={BookOpenText}
                    label="Topics"
                    value={totals.topics}
                    color="bg-purple-50 text-purple-600"
                />
                <SummaryTile
                    icon={HelpCircle}
                    label="Total Questions"
                    value={totals.questions}
                    color="bg-yellow-50 text-yellow-600"
                />
            </div>

            {/* Search */}
            <div className="relative mb-6">
                <Search
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                    type="text"
                    placeholder="Search by subject, year, or topic..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-white border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#2148C0]/30 focus:border-[#2148C0]"
                />
            </div>

            {error && (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm">
                    {error}
                </div>
            )}

            <SectionHeader accent="from-blue-500 to-indigo-500">
                Subjects
            </SectionHeader>
            <div className="mt-4">
                {loading ? (
                    <div className="space-y-3">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <div
                                key={i}
                                className="h-16 rounded-2xl bg-white border border-gray-100 animate-pulse"
                            />
                        ))}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-gray-300 bg-white py-16 text-center text-gray-500">
                        No topics match that search.
                    </div>
                ) : (
                    <div className="space-y-3">
                        {filtered.map((subject) => {
                            const open = !!expandedSubjects[subject.subject];
                            return (
                                <div
                                    key={subject.subject}
                                    className="rounded-2xl bg-white border border-gray-100 shadow-sm overflow-hidden"
                                >
                                    <button
                                        onClick={() => toggleSubject(subject.subject)}
                                        className="w-full flex items-center justify-between p-4 md:p-5 hover:bg-blue-50/40 transition-colors text-left"
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-[#2148C0]">
                                                <BookOpenText size={18} />
                                            </span>
                                            <div>
                                                <p className="text-base font-bold text-gray-900">
                                                    {subject.subject}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {subject.years.length}{" "}
                                                    {subject.years.length === 1 ? "year" : "years"} ·{" "}
                                                    {subject.total.toLocaleString()} questions
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="hidden sm:inline-flex px-2.5 py-1 rounded-full bg-blue-50 text-[#2148C0] text-xs font-semibold">
                                                {subject.total.toLocaleString()} total
                                            </span>
                                            {open ? (
                                                <ChevronDown size={18} className="text-gray-400" />
                                            ) : (
                                                <ChevronRight size={18} className="text-gray-400" />
                                            )}
                                        </div>
                                    </button>

                                    {open && (
                                        <div className="border-t border-gray-100 bg-gray-50/60">
                                            {subject.years.map((year) => {
                                                const yk = `${subject.subject}::${year.yearLabel}`;
                                                const yopen = !!expandedYears[yk];
                                                return (
                                                    <div
                                                        key={yk}
                                                        className="border-b last:border-b-0 border-gray-100"
                                                    >
                                                        <button
                                                            onClick={() => toggleYear(yk)}
                                                            className="w-full flex items-center justify-between px-5 py-3 hover:bg-white transition-colors text-left"
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-50 text-purple-600">
                                                                    <Calendar size={14} />
                                                                </span>
                                                                <div>
                                                                    <p className="text-sm font-semibold text-gray-800">
                                                                        {year.yearLabel}
                                                                    </p>
                                                                    <p className="text-xs text-gray-500">
                                                                        {year.topics.length || 0} topics ·{" "}
                                                                        {year.total.toLocaleString()} questions
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-3">
                                                                <span className="px-2 py-0.5 rounded-full bg-white border border-gray-200 text-xs text-gray-700 font-medium">
                                                                    {year.source === "exam" ? "Exam" : "Class"}
                                                                </span>
                                                                {year.topics.length > 0 ? (
                                                                    yopen ? (
                                                                        <ChevronDown size={16} className="text-gray-400" />
                                                                    ) : (
                                                                        <ChevronRight size={16} className="text-gray-400" />
                                                                    )
                                                                ) : (
                                                                    <span className="w-4" />
                                                                )}
                                                            </div>
                                                        </button>

                                                        {yopen && year.topics.length > 0 && (
                                                            <div className="px-5 pb-4 pt-1">
                                                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                                                    {year.topics.map((t) => (
                                                                        <div
                                                                            key={`${yk}::${t.topic}`}
                                                                            className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-white border border-gray-200"
                                                                        >
                                                                            <span className="text-sm text-gray-700 truncate">
                                                                                {t.topic}
                                                                            </span>
                                                                            <span className="text-xs font-semibold bg-blue-50 text-[#2148C0] px-2 py-0.5 rounded-full">
                                                                                {t.total.toLocaleString()}
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

const SummaryTile = ({ icon: Icon, label, value, color }) => (
    <div className="flex items-center gap-4 rounded-2xl bg-white border border-gray-100 p-5 shadow-sm">
        <span className={`flex h-12 w-12 items-center justify-center rounded-xl ${color}`}>
            <Icon size={20} />
        </span>
        <div>
            <p className="text-xs uppercase tracking-[0.16em] text-gray-500 font-semibold">
                {label}
            </p>
            <p className="text-3xl font-bold text-gray-900 mt-0.5">
                {Number(value || 0).toLocaleString()}
            </p>
        </div>
    </div>
);

export default TopicsPage;
